import { useEffect, useRef } from 'react';

export interface SelfReportCreatedPayload {
  type: 'self-report.created';
  id: number;
  playerId: number;
  playerName: string;
  submittedAt: string;
  autoApproved: boolean;
}

export function useSelfReportStream({
  enabled,
  onCreated,
}: {
  enabled: boolean;
  onCreated: (payload: SelfReportCreatedPayload) => void;
}) {
  const handlerRef = useRef(onCreated);
  handlerRef.current = onCreated;

  useEffect(() => {
    if (!enabled) return;
    let es: EventSource | null = null;
    let closed = false;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let backoff = 2_000;

    const connect = () => {
      if (closed) return;
      es = new EventSource('/api/self-reports/stream', {
        withCredentials: true,
      });
      es.addEventListener('self-report.created', (ev: MessageEvent) => {
        try {
          const data = JSON.parse(ev.data) as SelfReportCreatedPayload;
          handlerRef.current(data);
        } catch {
          // ignore malformed payloads
        }
      });
      es.onopen = () => {
        backoff = 2_000;
      };
      es.onerror = () => {
        es?.close();
        es = null;
        if (closed) return;
        retry = setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, 30_000);
      };
    };

    connect();

    return () => {
      closed = true;
      if (retry) clearTimeout(retry);
      es?.close();
      es = null;
    };
  }, [enabled]);
}
